import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { getProducts } from '../../api/master'
import { useSession } from '../../session'
import { productLabel } from './purchaseShared'

// With Production: purchases for goods to resell, or for raw materials kept
// to make things. What an item is for belongs to the product (Bought to
// resell or Material), so a document can hold both; this only filters the
// lists, kept in the URL (?for=) so it follows you from tab to tab.

const OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'resale', label: 'For resale' },
  { id: 'materials', label: 'Materials' },
]

export function useItemFor() {
  const { has } = useSession()
  const [params, setParams] = useSearchParams()
  const on = has('production')
  const raw = params.get('for')
  const value = on && (raw === 'resale' || raw === 'materials') ? raw : 'all'
  const set = (id) => setParams((p) => {
    const n = new URLSearchParams(p)
    if (id === 'all') n.delete('for'); else n.set('for', id)
    return n
  }, { replace: true })
  const matches = (row) => value === 'all' || (row?.item_kinds || []).includes(value)
  // For links to a new document from a filtered list ("?for=materials").
  const query = value === 'all' ? '' : `?for=${value}`
  return { on, value, set, matches, query }
}

/** A list page's status filter, keeping ?for= (and anything else) as it is. */
export function setParam(setParams, key, id) {
  setParams((p) => {
    const n = new URLSearchParams(p)
    if (id === 'all') n.delete(key); else n.set(key, id)
    return n
  }, { replace: true })
}

/** All / For resale / Materials, with how many of each. */
export function ItemForToggle({ itemFor, rows }) {
  if (!itemFor.on) return null
  const count = (id) => (rows || []).filter((r) => id === 'all' || (r.item_kinds || []).includes(id)).length
  return (
    <div className="segmented compact" role="radiogroup" aria-label="What it’s for">
      {OPTIONS.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={itemFor.value === o.id}
          className={itemFor.value === o.id ? 'active' : ''} onClick={() => itemFor.set(o.id)}>
          <span>{o.label} <span className="segmented-count">{count(o.id)}</span></span>
        </button>
      ))}
    </div>
  )
}

/** "Material" next to a line's product, for a raw material. */
export function MaterialTag({ kind }) {
  return kind === 'material' ? <span className="kind-tag">Material</span> : null
}

/** With Production: the ids of products that are raw materials, for tagging
 *  lines (shares the products list other pages load). */
export function useMaterialIds() {
  const { has } = useSession()
  const on = has('production')
  const { data = [] } = useQuery({ queryKey: ['products'], queryFn: () => getProducts(), enabled: on })
  return new Set(on ? (data || []).filter((p) => p.stock_kind === 'material').map((p) => Number(p.product_id)) : [])
}

/** A product dropdown's options: with Production and any materials, grouped
 *  into Materials and For resale (materials first when buying materials). */
export function ProductOptions({ products, materialsFirst = false }) {
  const { has } = useSession()
  const opt = (p) => <option key={p.product_id} value={p.product_id}>{productLabel(p)}</option>
  const mats = (products || []).filter((p) => p.stock_kind === 'material')
  if (!has('production') || !mats.length) return <>{(products || []).map(opt)}</>
  const resale = (products || []).filter((p) => p.stock_kind !== 'material')
  const groups = [<optgroup key="m" label="Materials">{mats.map(opt)}</optgroup>, <optgroup key="r" label="For resale">{resale.map(opt)}</optgroup>]
  return <>{materialsFirst ? groups : groups.reverse()}</>
}
